// FF-DOCK-2 — sidebar Firefly glow from intent + visual-intensity tokens.

import type { CSSProperties } from "react";
import type { FireflyIntent } from "@/components/activity/firefly/fireflyIntents";
import { getFireflyIntentParams } from "@/components/activity/firefly/fireflyIntents";
import type { FireflyVisualIntensity } from "@/lib/firefly/fireflyVisualIntensity";
import type { FireflyDisplayMode } from "@/lib/firefly/fireflyDisplayMode";
import { isFireflySidebarDockTravelActive } from "@/lib/firefly/fireflySidebarDockTravel";

/** Floating highlight + discovery chrome only in floating display mode. */
export function shouldShowFloatingFireflyHost(mode: FireflyDisplayMode): boolean {
  return mode === "floating" && !isFireflySidebarDockTravelActive();
}

/** Floating dock Firefly tab — hidden when Firefly is housed in the bell. */
export function shouldShowFloatingFireflyTab(mode: FireflyDisplayMode): boolean {
  return mode === "floating" && !isFireflySidebarDockTravelActive();
}

/** Outer glow blur scales with per-user visual intensity (smoke lock). */
export function fireflyDockGlowBlurPx(intensity: FireflyVisualIntensity): number {
  switch (intensity) {
    case "calm":
      return 8;
    case "normal":
      return 14;
    case "lively":
      return 22;
  }
}

export function fireflyDockGlowColor(intent: FireflyIntent): string {
  return getFireflyIntentParams(intent)?.glowColor ?? "rgba(52, 211, 153, 0.9)";
}

/** Alert sparks around the sidebar Firefly chip — skip ambient Focus. */
export function shouldShowSidebarFireflyUrgencySparks(input: {
  intent: FireflyIntent;
  reducedMotion?: boolean;
  glowSuppressed?: boolean;
}): boolean {
  if (input.glowSuppressed || input.reducedMotion) return false;
  return input.intent !== "focus";
}

export function buildSidebarFireflyDockGlowStyle(input: {
  intent: FireflyIntent;
  intensity?: FireflyVisualIntensity;
  reducedMotion?: boolean;
}): CSSProperties {
  const glow = fireflyDockGlowColor(input.intent);
  const intensity = input.intensity ?? "normal";
  const blur = fireflyDockGlowBlurPx(intensity);
  if (input.reducedMotion) {
    return {
      borderColor: glow,
      boxShadow: `inset 0 0 0 1px ${glow}`,
    };
  }
  return {
    borderColor: glow,
    boxShadow: `inset 0 0 0 1px ${glow}, 0 0 ${blur}px ${glow}`,
  };
}
